import InitApi from "api/InitApi";
import ServerErrors from "components/SeverErrors";
import { useUrlQuery } from "hooks/useUrlQuery";
import { useMutation, useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import toast from "react-hot-toast";
import useTokenStore from "store/tokenStore";
import { persistAuthSession } from "utils/persistAuthSession";
import { deviceIdentifiers } from "utils";

export const isUnverifiedEmailHeldConflict = (error) => {
	const status = error?.response?.status;
	const code = error?.response?.data?.data?.code;
	return status === 409 && code === "unverified_email_held";
};

/**
 * Exchange a Google ID token or Facebook access token for a library JWT session.
 * Callers should handle unverified_email_held (409) themselves.
 */
const useSocialLogin = () => {
	const query = useUrlQuery();
	const queryClient = useQueryClient();
	const { accessToken } = useTokenStore();
	const history = useHistory();

	const socialLogin = async ({ provider, access_token, id_token }) => {
		const instance = await InitApi({ accessToken });
		const { os, browser } = deviceIdentifiers();
		const response = await instance.post(`/auth/social/${provider}`, {
			...(access_token ? { access_token } : {}),
			...(id_token ? { id_token } : {}),
			os: os || "Unknown",
			browser: browser || "Unknown",
			device: "web",
		});
		return response;
	};

	return useMutation(socialLogin, {
		onSuccess: async ({ data: body }) => {
			persistAuthSession(body, {
				queryClient,
				history,
				intended: query?.get("intended"),
			});
		},
		onError: async (error) => {
			if (isUnverifiedEmailHeldConflict(error)) {
				return;
			}
			toast.error(
				<ServerErrors
					errorMessage={error?.response?.data?.message}
					errors={error?.response?.data?.data}
				/>,
				{
					className: "max-w-max",
				},
			);
		},
	});
};

/**
 * Finish Google sign-in after the person chooses link or separate.
 */
export const useResolveSocialConflict = () => {
	const query = useUrlQuery();
	const queryClient = useQueryClient();
	const { accessToken } = useTokenStore();
	const history = useHistory();

	const resolveConflict = async ({ claim_token, resolution, email, password }) => {
		const instance = await InitApi({ accessToken });
		const { os, browser } = deviceIdentifiers();
		const response = await instance.post(`/auth/social/google/resolve`, {
			claim_token,
			resolution,
			...(email ? { email } : {}),
			...(password ? { password } : {}),
			os: os || "Unknown",
			browser: browser || "Unknown",
			device: "web",
		});
		return response;
	};

	return useMutation(resolveConflict, {
		onSuccess: async ({ data: body }) => {
			persistAuthSession(body, {
				queryClient,
				history,
				intended: query?.get("intended"),
			});
		},
	});
};

export default useSocialLogin;
